/**
 * The web media pipeline behind the MediaResolver primitive context: file URLs from the API get the workspace's file
 * read token appended (img/video src and download links can't send a bearer token), pending uploads map to object
 * URLs of their local bytes, and a just-sent file keeps showing from those bytes instead of downloading again.
 */
import type { FileRef } from "@bundle/contracts";
import type { ApiClient, KeyValueStorage } from "@bundle/core";
import type { UploadManager } from "./uploads.ts";

type Token = { token: string; expiresAt: number };

const FILE_PATH = /^\/v1\/w\/([^/?#]+)\/files\/([^/?#]+)(\/thumb)?(?:[?#]|$)/;
/** Mail sender pictures (core `Mail.mailAvatarPath`): same read token, nothing local. */
const MAIL_AVATAR_PATH = /^\/v1\/w\/([^/?#]+)\/mail\/avatars\/[^/?#]+$/;
const REFRESH_BEFORE_MS = 60 * 60_000;
const STORAGE_KEY = "fileTokens";

export class MediaUrls {
  private tokens = new Map<string, Token>();
  private readonly fetching = new Set<string>();
  private readonly listeners = new Set<() => void>();
  private v = 0;
  /** Extra resolver for mock-mode `mockfile:` URLs. */
  mockFile: ((id: string) => string | undefined) | undefined;
  /** Mock mode: maps other sources (the fake GIF provider's CDN URLs) to local ones; undefined = not handled. */
  mockRewrite: ((src: string) => string | undefined) | undefined;

  constructor(
    private readonly api: ApiClient,
    private readonly base: string,
    private readonly uploads: UploadManager,
    private readonly storage: KeyValueStorage,
  ) {
    try {
      const saved = JSON.parse(storage.get(STORAGE_KEY) ?? "{}") as Record<string, Token>;
      for (const [ws, t] of Object.entries(saved)) if (t.expiresAt > Date.now()) this.tokens.set(ws, t);
    } catch {
      // ignore
    }
    uploads.subscribe(() => this.notify());
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly version = (): number => this.v;

  readonly resolve = (src: string): string | undefined => {
    if (src.startsWith("local-upload:")) return this.uploads.localUrl(src.slice("local-upload:".length));
    if (src.startsWith("mockfile:")) return this.mockFile?.(src.slice("mockfile:".length));
    const rewritten = this.mockRewrite?.(src);
    if (rewritten !== undefined) return rewritten;
    const avatar = MAIL_AVATAR_PATH.exec(src);
    if (avatar !== null) return this.withToken(src, avatar[1]!);
    const m = FILE_PATH.exec(src);
    if (m === null) return src;
    if (m[3] === undefined) {
      const local = this.uploads.localUrl(decodeURIComponent(m[2]!));
      if (local !== undefined) return local;
    }
    return this.withToken(src, decodeURIComponent(m[1]!));
  };

  /** A URL that downloads the original as an attachment. */
  downloadUrl(file: FileRef): string | undefined {
    if (file.url.startsWith("mockfile:") || file.url.startsWith("local-upload:")) return this.resolve(file.url);
    const url = this.withToken(file.url, FILE_PATH.exec(file.url)?.[1] ?? "");
    return url === undefined ? undefined : `${url}&download=1`;
  }

  /** Waits for the workspace's token (download clicked before it arrived). */
  async ready(ws: string): Promise<void> {
    if (this.tokens.has(ws)) return;
    await this.fetch(ws);
  }

  /** Re-render media (new local bytes, e.g. the mock store finished loading). */
  refresh(): void {
    this.notify();
  }

  clear(): void {
    this.tokens.clear();
    this.storage.remove(STORAGE_KEY);
    this.notify();
  }

  private withToken(src: string, wsEncoded: string): string | undefined {
    const ws = decodeURIComponent(wsEncoded);
    const t = this.tokens.get(ws);
    if (t === undefined || t.expiresAt - Date.now() < REFRESH_BEFORE_MS) void this.fetch(ws);
    if (t === undefined || t.expiresAt <= Date.now()) return undefined;
    return `${this.base}${src}${src.includes("?") ? "&" : "?"}t=${encodeURIComponent(t.token)}`;
  }

  private async fetch(ws: string): Promise<void> {
    if (ws === "" || this.fetching.has(ws)) return;
    this.fetching.add(ws);
    try {
      const t = await this.api.fileToken(ws);
      this.tokens.set(ws, t);
      this.storage.set(STORAGE_KEY, JSON.stringify(Object.fromEntries(this.tokens)));
      this.notify();
    } catch {
      // Offline or signed out: a later render asks again.
      setTimeout(() => this.fetching.delete(ws), 15_000);
      return;
    }
    this.fetching.delete(ws);
  }

  private notify(): void {
    this.v += 1;
    for (const l of Array.from(this.listeners)) l();
  }
}
